/**
 * Слова и формы экрана «Вода»: чистые функции без React и БЕЗ арифметики над м³.
 *
 * Все числа воды считает сервер (change irrigation-water-events, С4): хозяйства
 * полива и их недоподача по правилу №222 (`water_queries.farm_gap_m3`),
 * «канал → хозяйства» (№651), покрытие «подано от запрошенного», доля воды
 * цикла, расхождения сверки. Раньше их складывал браузер, и клиентская
 * недоподача расходилась с серверной; теперь здесь остались только имена и
 * выбор верхушки списка для диаграммы.
 *
 * Типы строк (`FarmWaterRow`, `ChannelRow`, `Shortfall`) живут рядом с
 * разбором ответа — `data-source/api/water.ts`; здесь они переэкспортированы
 * под прежними именами.
 */
import type { SeasonPicture } from '../../api/client';
import { decimal } from '../data-source/api/dataset';
import { defineMessages } from '../../i18n/define';
import { tr } from '../../i18n/runtime';

export type {
  ChannelRow,
  FarmWaterRow,
  FarmWaterState,
  Shortfall,
} from '../data-source/api/water';
import type { FarmWaterRow, Shortfall } from '../data-source/api/water';

/** Двусторонний разрез: крупнейшие недоподачи и перерасходы — только выбор верхушки готовых строк. */
export function divergingFarms(rows: readonly FarmWaterRow[], shortfall: Shortfall | null, top = 8) {
  const under = rows.filter((r) => (r.shortM3 ?? 0) > 0).sort((a, b) => b.shortM3! - a.shortM3!);
  const over = rows.filter((r) => (r.overM3 ?? 0) > 0).sort((a, b) => b.overM3! - a.overM3!);
  const head = { under: under.slice(0, top), over: over.slice(0, top) };
  // Шкала столбиков — масштаб рисунка, а не показатель: берётся по самой длинной полосе.
  const max = Math.max(1, ...head.under.map((r) => r.shortM3!), ...head.over.map((r) => r.overM3!));
  return { ...head, underCount: shortfall?.underFarms ?? under.length, overCount: shortfall?.overFarms ?? over.length, max };
}

/** «Хлопок-2026: Полив №5 (последний, перед дефолиацией)» → имя и пояснение. */
export function wateringTitle(name: string): { title: string; note: string | null } {
  const tail = name.includes(':') ? name.slice(name.indexOf(':') + 1).trim() : name.trim();
  const m = /^(.*?)\s*\((.+)\)\s*$/.exec(tail);
  if (m && m[1]) return { title: m[1], note: m[2]! };
  return { title: tail || name, note: null };
}

/* ------------------------------------------- вода цикла: одна доля на экраны */

const m = defineMessages({
  shareWords: {
    id: 'water.summary.shareWords',
    defaultMessage: 'подано от запрошенного',
    description: 'Подпись доли воды агроцикла (подано / запрошено), одна на «Обзоре района» и на «Воде»; строчными, после значения, коротко',
  },
  shareMethod: {
    id: 'water.summary.shareMethod',
    defaultMessage:
      'Подано / запрошено по журналам ирригации среза агроцикла, включая заявки, по которым подачи ещё не было. Недоподача — сумма по хозяйствам, где подано меньше запрошенного (хозяйство с заявкой и без единой подачи — вся заявка): перерасход одних хозяйств её не гасит. Числа считает сервер.',
    description: 'Текст всплывающего пояснения «Как считается «Вода»» на «Обзоре района» и на «Воде»: как считается доля воды агроцикла и недоподача',
  },
});

/** Подпись доли воды агроцикла — одна на «Обзоре района» и на «Воде». */
export function waterShareWords(): string {
  return tr(m.shareWords);
}

/** Как считается доля воды агроцикла — одни слова для подсказок обоих экранов. */
export function waterShareMethod(): string {
  return tr(m.shareMethod);
}

/**
 * @deprecated Строка застывает на языке загрузки — берите `waterShareWords()`.
 * Оставлено, пока `DistrictOverview.tsx` не переведён на функцию.
 */
// i18n-ignore: устаревший экспорт, заменён функцией waterShareWords()
export const WATER_SHARE_WORDS = 'подано от запрошенного';

/**
 * @deprecated Строка застывает на языке загрузки — берите `waterShareMethod()`.
 * Оставлено, пока `DistrictOverview.tsx` не переведён на функцию.
 */
// i18n-ignore: устаревший экспорт, заменён функцией waterShareMethod()
export const WATER_SHARE_METHOD = 'Подано / запрошено по журналам ирригации среза агроцикла, включая заявки, по которым подачи ещё не было. Недоподача — сумма по хозяйствам, где подано меньше запрошенного (хозяйство с заявкой и без единой подачи — вся заявка): перерасход одних хозяйств её не гасит. Числа считает сервер.';

export interface CycleWaterShare {
  requestedM3: number | null;
  deliveredM3: number | null;
  /** Подано от запрошенного, % — с сервера; `null` — делить не на что. */
  sharePct: number | null;
  /** Недоподача по хозяйствам, м³ — с сервера (№222); `null` — ни у одного хозяйства разрыв не посчитан. */
  shortfallM3: number | null;
}

/**
 * Вода цикла по картине сезона (`/season/picture`, `summary=true`) — ОДНО
 * определение для «Обзора района» и «Воды» (6al.13). Доля и недоподача — поля
 * ответа (`water.coverage_pct`, `summary.water_shortfall_m3`): сервер считает
 * их той же функцией, что `season_report` и метрика `water.shortfall_th_m3`.
 */
export function cycleWaterShare(picture: SeasonPicture | null | undefined): CycleWaterShare | null {
  if (!picture) return null;
  return {
    requestedM3: decimal(picture.water.requested_volume_m3),
    deliveredM3: decimal(picture.water.delivered_volume_m3),
    sharePct: picture.water.coverage_pct ?? null,
    shortfallM3: picture.summary?.water_shortfall_m3 ?? null,
  };
}
